import React, { useState, useMemo } from 'react';
import DataGrid from '../grid/DataGrid';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ClientContact, VendorContact } from '../../types';
import { Edit, Trash2, Mail, Phone, UserCheck, UserPlus } from 'lucide-react';

interface ContactGridProps {
  contacts: (ClientContact | VendorContact)[];
  isLoading: boolean;
  onEdit: (contact: ClientContact | VendorContact) => void;
  onDelete: (contact: ClientContact | VendorContact) => void;
  onSetPrimary: (contact: ClientContact | VendorContact) => void;
  onAddNew: () => void;
  contactType: 'client' | 'vendor';
}

export const ContactGrid: React.FC<ContactGridProps> = ({
  contacts,
  isLoading,
  onEdit,
  onDelete,
  onSetPrimary,
  onAddNew,
  contactType
}) => {
  // Search state
  const [searchTerm, setSearchTerm] = useState('');

  // Filter contacts based on search term
  const filteredContacts = useMemo(() => {
    if (!searchTerm.trim()) return contacts;
    
    const term = searchTerm.toLowerCase();
    return contacts.filter(contact => 
      contact.fullName?.toLowerCase().includes(term) ||
      contact.email?.toLowerCase().includes(term) ||
      contact.phone?.toLowerCase().includes(term) ||
      contact.mobile?.toLowerCase().includes(term) ||
      contact.title?.toLowerCase().includes(term)
    );
  }, [contacts, searchTerm]);
  const columns = useMemo(() => [
    {
      id: 'name',
      header: 'Name',
      sortable: true,
      cell: (value: any, row: ClientContact | VendorContact) => (
        <div className="flex items-center space-x-2">
          <div>
            <div className="font-medium text-gray-900">{row.fullName}</div>
            {row.title && (
              <div className="text-sm text-gray-500">{row.title}</div>
            )}
          </div>
          {row.isPrimary && (
            <Badge variant="primary" size="sm">
              Primary
            </Badge>
          )}
        </div>
      )
    },
    {
      id: 'email',
      header: 'Email',
      sortable: true,
      cell: (value: any, row: ClientContact | VendorContact) => (
        <div className="text-sm text-gray-900">
          {row.email ? (
            <a 
              href={`mailto:${row.email}`}
              className="text-accent-color hover:text-accent-color-800 flex items-center space-x-1"
            >
              <Mail className="h-4 w-4" />
              <span>{row.email}</span>
            </a>
          ) : (
            <span className="text-gray-400">-</span>
          )}
        </div>
      )
    },
    {
      id: 'phone',
      header: 'Phone',
      sortable: false,
      cell: (value: any, row: ClientContact | VendorContact) => (
        <div className="space-y-1">
          {row.phone && (
            <div className="text-sm text-gray-900 flex items-center space-x-1">
              <Phone className="h-4 w-4" />
              <span>{row.phone}</span>
            </div>
          )}
          {row.mobile && (
            <div className="text-sm text-gray-900 flex items-center space-x-1">
              <Phone className="h-4 w-4" />
              <span className="text-accent-color">{row.mobile}</span>
            </div>
          )}
          {!row.phone && !row.mobile && (
            <span className="text-gray-400 text-sm">-</span>
          )}
        </div>
      )
    },
    {
      id: 'status',
      header: 'Status',
      sortable: true,
      cell: (value: any, row: ClientContact | VendorContact) => (
        <div className="relative group">
          <div className="group-hover:opacity-0 transition-opacity duration-200">
            <Badge variant={row.isActive ? 'success' : 'secondary'}>
              {row.isActive ? 'Active' : 'Inactive'}
            </Badge>
          </div>
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-end gap-2 pr-2" onClick={(e)=>e.stopPropagation()}>
            {!row.isPrimary && (
              <button 
                onClick={() => onSetPrimary(row)}
                className="h-7 px-2 inline-flex items-center border rounded-md text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
                title="Set Primary"
              >
                <UserCheck className="h-4 w-4"/>
              </button>
            )}
            <button 
              onClick={() => onEdit(row)}
              className="h-7 px-2 inline-flex items-center border rounded-md text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
              title="Edit Contact"
            >
              <Edit className="h-4 w-4"/>
            </button>
            <button 
              onClick={() => onDelete(row)}
              className="h-7 px-2 inline-flex items-center border rounded-md text-red-600 border-red-300 hover:bg-red-50"
              title="Delete Contact"
            >
              <Trash2 className="h-4 w-4"/>
            </button>
          </div>
        </div>
      )
    }
  ], []);

  return (
    <DataGrid
        data={filteredContacts}
        columns={columns}
        isLoading={isLoading}
        searchTerm={searchTerm}
        onSearch={setSearchTerm}
        currentPage={1}
        pageSize={3}
        totalCount={filteredContacts.length}
        totalPages={Math.ceil(filteredContacts.length / 3)}
        onPageChange={() => {}}
        searchPlaceholder="Search contacts..."
        emptyMessage="No contacts found"
        onRowClick={() => {}}
        maxHeight="300px"
        getRowClassName={() => 'group'}
      />
  );
};
